import React from "react";
import "./FlashText.css";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Long strings flash by word; short ones by character. */
const CHAR_LIMIT = 90;

/**
 * Hard ceiling on the whole stagger. Without this a long paragraph takes over
 * a second to finish and the card reads as empty while you are looking at it.
 */
const MAX_STAGGER_MS = 420;

type FlashTextProps = {
  text: string;
  /** ms between successive units lighting up. */
  stepMs?: number;
  /** Force the unit instead of letting length decide. */
  by?: "char" | "word";
  className?: string;
};

/**
 * Flashes text in one unit at a time — each character (or word) arrives with a
 * brief overshoot before settling, so the line writes itself on.
 *
 * Runs once, when it first scrolls into view. Long copy switches to word units:
 * a 300-character paragraph would otherwise mean 300 spans per card, and at
 * reading distance the two are visually indistinguishable anyway.
 */
const FlashText: React.FC<FlashTextProps> = ({
  text,
  stepMs = 14,
  by,
  className = "",
}) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [lit, setLit] = React.useState(() => prefersReducedMotion());

  const unit = by ?? (text.length > CHAR_LIMIT ? "word" : "char");

  const pieces = React.useMemo(
    () => (unit === "word" ? text.split(/(\s+)/) : text.split("")),
    [text, unit]
  );

  // Compress the step for long runs so the total never exceeds the ceiling.
  const step = Math.min(stepMs, MAX_STAGGER_MS / Math.max(1, pieces.length));

  React.useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;

    // Safety net: text must never stay invisible. IntersectionObserver can
    // miss under StrictMode's mount/unmount/remount, and a missed callback
    // here means the string never renders at all. Reveal regardless after a
    // beat — worst case an off-screen element animates early.
    const failsafe = window.setTimeout(() => setLit(true), 900);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setLit(true);
          observer.disconnect();
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(el);
    return () => {
      window.clearTimeout(failsafe);
      observer.disconnect();
    };
  }, []);

  return (
    <span
      ref={ref}
      className={`flash-text${lit ? " flash-text--lit" : ""} ${className}`.trim()}
      aria-label={text}
    >
      {pieces.map((piece, i) => (
        <span
          key={i}
          aria-hidden="true"
          className="flash-text__unit"
          style={{ animationDelay: `${Math.round(i * step)}ms` }}
        >
          {piece}
        </span>
      ))}
    </span>
  );
};

export default FlashText;
